import { compose } from '@varos/util-typescript';
import { Outlet, Route } from 'react-router';
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import theme from '../../../styles/theme';
import { FormatterImpl } from '../../../hooks';
import { createPageControllerV2 } from '../../view/page';
import { EnvironmentVariables } from '../../config/environment';
import { createAlertListController, createFormController } from '../../view/common';
import { createErrorKernelEnhancer, createKernelTrackingStrategy } from '../../strategy';
import { createKernelToastEnhancer } from '../../middleware';
import { InterviewEntrypointConfig } from './interviewEntrypointConfig';
import { InterviewEntry } from './interviewEntrypointInterface';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// TODO extract out of research context
import { createCommonUI } from '../../module/research/route/document/view';

const customTheme = {};

export function createInterviewEntrypoint(
    config: InterviewEntrypointConfig
): InterviewEntry {
    const axios = config.infra.createAxios({
        defaults: {},
        async getToken() {
            return null;
        },
    });

    const infra = {
        help: config.infra.createHelp({
            getClient: config.infra.getIntercom,
        }),
        getIntercom: config.infra.getIntercom,
        errorFormatter: config.infra.createErrorFormatter(),
        formatter: config.infra.createFormatter(),
        axios: axios,
        formatterOld: new FormatterImpl(),
        optionParser: config.infra.createUrlOptionParser(),
        useTracker: config.infra.createUseTracker(),
        toaster: config.infra.createToaster(),
        repository: config.infra.createRepository(),
        sentry: config.infra.createSentry(),
        queryClient: new QueryClient(),
    };

    const kernel = config.infra.createKernel(
        {
            adapter: {
                authenticator: {
                    async getToken(context) {
                        return { token: 'NOT SET' };
                    },
                    async logout(context) {
                        return;
                    },
                },
                help: infra.help,
            },
            provider: {
                createFormController: createFormController,
                createPageController(controllerConfig) {
                    // TODO clean this up
                    return createPageControllerV2({
                        ...controllerConfig,
                        provider: {
                            createAlertListController: createAlertListController,
                        },
                    });
                },
            },
            infra: {
                optionParser: infra.optionParser,
                toaster: infra.toaster,
                formatter: infra.formatterOld,
                useTracker: infra.useTracker,
                repository: infra.repository,
            },
            navigation: {
                rootPath: config.mount,
            },
            help: {
                adapter: infra.help,
                exclude: [config.mount],
            },
            overlay: {
                stateKey: 'modal',
            },
        },
        compose(
            createKernelTrackingStrategy(),
            createKernelToastEnhancer(),
            createErrorKernelEnhancer({ client: infra.sentry })
        )
    );

    const view = {
        application: config.common.createApplication(),
    };
    const hooks = {};

    const api = {
        assistant: config.api.createAssistant({
            baseUrl: EnvironmentVariables.INTERVIEW_API_BASE_URL,
            axios,
        }),
    };

    const repository = {
        assistant: config.repository.createAssistant({
            kernel: {
                repository: kernel.infra.repository,
            },
            api: api.assistant,
        }),
    };

    const UI = {
        Application: config.ui.createApplication(),
        Common: createCommonUI(),
    };

    const assistantRouter = config.router.createAssistant({
        mount: config.mount,
        kernel,
        controller: view.application,
        adapter: {
            user: {
                useUser() {
                    return null;
                },
            },
        },
        // hook: {
        //     useAuth() {
        //         return {
        //             user: null,
        //             scheme: { kind: 'apiKey', token: null, assume: null },
        //         };
        //     },
        //     useAccount: null,
        // },
        UI: {
            Application: UI.Application,
            Common: UI.Common,
        },
        infra: {
            navigation: {
                legal: {
                    termsOfService(props) {
                        return { to: `/legal/terms-of-service` };
                    },
                },
            },
        },
        repository: repository.assistant,
    });

    const { Container: HelpContainer } = kernel.infra.help;

    return {
        root: (
            <Route
                element={
                    <ChakraProvider theme={theme}>
                        <QueryClientProvider client={infra.queryClient}>
                            <Outlet />
                            {/* register intercom handling logic */}
                            <HelpContainer />
                        </QueryClientProvider>
                    </ChakraProvider>
                }
            >
                {assistantRouter.element}
            </Route>
        ),
    };
}
